import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'

import usePagedView from '@/modules/signature/_helpers/usePagedView'
import SignatureModal from '@/modules/signature/components/signature-modal'
import { SIGNATURE_FIELD_TYPES } from '@/modules/base/util/constants'
import type { SignatureContextValue, PageSize, SignatureItemLocal, AddItemArgs, TodoLike, MetaDataEntry } from '@/modules/signature/_helpers/SignatureContextTypes'
import { useAppSelector } from '@/redux/store'

export const SIGNATURE_LIMIT = 100

interface SignatureContextProviderProps {
  children: ReactNode,
}

interface ModalState {
  open: boolean,
}

const SignatureContext = createContext<SignatureContextValue | null>(null)

const SignatureContextProvider = (props: SignatureContextProviderProps) => {
  const { children } = props

  const [items, setItems] = useState<SignatureItemLocal[]>([])
  const [currentItemIndex, setCurrentItemIndex] = useState<number>(0)
  const [popoverOpenForIndex, setPopoverOpenForIndex] = useState<number | undefined>()
  const [allSigned, setAllSigned] = useState<boolean>(false)
  const [fileName, setFileName] = useState<string>('')
  const [signingMode, setSigningMode] = useState<boolean>(false)
  const [placingMode, setPlacingMode] = useState<boolean>(false)
  const [pageSize, setPageSize] = useState<PageSize>({ width: 0, height: 0, originalWidth: 0, originalHeight: 0 })
  const [modalState, setModalState] = useState<ModalState>({ open: false })
  const [pageScaleFactor, setPageScaleFactor] = useState<number>(1)
  const [scrollIntoView, setScrollIntoView] = useState<number | undefined>()
  const { currentPage, nextPage, prevPage, numPages, setNumPages, setCurrentPage } = usePagedView()

  const userEmail = useAppSelector(state => state.user.info.email)
  const isTaxConsultant = useAppSelector(state => state.tenant.info.isTaxConsultant)

  const allSignatureItemsDone = () => items.filter(s => !s.signature && !s.date && !s.text).length === 0
  const checkSignatureItemStatus = () => {
    if (!allSignatureItemsDone()) {
      setAllSigned(false)
    } else {
      setAllSigned(true)
    }
  }
  useEffect(checkSignatureItemStatus, [items])
  useEffect(() => {
    if (!placingMode) { return }
    items.map((item) => {
      const updatedItem = {
        ...item,
        pageSize: { ...pageSize },
      }
      return updatedItem
    }, [])
  }, [pageSize])

  const resetContext = () => {
    setNumPages(undefined)
    setCurrentPage(1)
    setItems([])
    setCurrentItemIndex(0)
    setAllSigned(false)
    setFileName('')
    setSigningMode(false)
    setPlacingMode(false)
    setScrollIntoView(undefined)
  }

  const resetFields = () => {
    setItems([])
  }

  const getItems = (todo: TodoLike): SignatureItemLocal[] => {
    const hasNewSignatures = Boolean(todo?.signatures?.length)

    if (hasNewSignatures) {
      return [...(todo.signatures || [])]
    } else {
      return todo?.metaData?.filter((i: MetaDataEntry) => i.id === 'signatures')?.[0]?.value || []
    }
  }

  const loadItems = (todo: TodoLike) => {
    const items = getItems(todo)
    if (items.length > 0) {
      setItems(items)
    }
  }
  const resetItem = (key: number) => {
    const updatedItems = [...items]
    const item = items[key]
    switch (item.type) {
      case SIGNATURE_FIELD_TYPES.DATE: {
        updatedItems[key] = { ...item, date: null }
        break
      }
      case SIGNATURE_FIELD_TYPES.TEXT: {
        updatedItems[key] = { ...item, text: null }
        break
      }
      default: {
        updatedItems[key] = { ...item, signature: undefined }
        break
      }
    }

    setItems(updatedItems)
  }
  const addItem = ({ responsibleUserName, page, type, ...restProps }: AddItemArgs) => {
    const defaultItem: SignatureItemLocal = {
      type,
      name: responsibleUserName,
      position: {
        page,
        x: 50,
        y: 50,
      },
      pageSize,
      ...restProps,
    }

    const updatedItems = [...items, defaultItem]

    const newCurrentItemIndex = updatedItems.length - 1
    setCurrentItemIndex(newCurrentItemIndex)
    setItems(updatedItems)

    if (SIGNATURE_FIELD_TYPES.TEXT === type) {
      setPopoverOpenForIndex(newCurrentItemIndex)
    }
  }
  const updateItem = (item: SignatureItemLocal) => {
    const updatedItems = [...items]
    updatedItems[currentItemIndex] = { ...item, pageSize: { ...pageSize } }
    setItems(updatedItems)
  }
  const addSigningToAll = (signature: string) => {
    const currentItem = items[currentItemIndex]
    const updatedItems = items
      .map((item) => {
        if ([SIGNATURE_FIELD_TYPES.DATE, SIGNATURE_FIELD_TYPES.TEXT].includes(item.type) || item.signerEmail !== currentItem.signerEmail) { return item }
        if (userEmail !== item.signerEmail && !isTaxConsultant) {
          return item
        }

        return { ...item, signature }
      })
    setItems(updatedItems)
  }
  const removeItem = (item: number) => {
    const updatedItems = [...items]
    delete updatedItems[item]
    setItems(updatedItems.filter((s) => !!s))
  }
  const handleSigning = ({ signature: base64Str }: { signature: string }, applyToAll?: boolean) => {
    setModalState({ open: false })
    if (applyToAll) { return addSigningToAll(base64Str) }
    updateItem({ ...items[currentItemIndex], signature: base64Str })
  }
  const openSigningModal = (index: number) => {
    setCurrentItemIndex(index)
    setModalState({ open: true })
  }
  // For text and date items it opens the related popover for the given index
  const openRelatedPopoverForIndex = (index: number) => {
    setCurrentItemIndex(index)
    setPopoverOpenForIndex(index)
  }

  const setPageSizeWithCalc = useCallback((size: PageSize) => {
    const scaleWidth = size.width / size.originalWidth
    const scaleHeight = size.height / size.originalHeight
    const scaleFactor = Math.min(scaleWidth, scaleHeight)
    setPageSize({
      height: parseFloat(size.height.toFixed(4)),
      width: parseFloat(size.width.toFixed(4)),
      originalWidth: parseFloat(size.originalWidth.toFixed(4)),
      originalHeight: parseFloat(size.originalHeight.toFixed(4)),
    })
    setPageScaleFactor(parseFloat(scaleFactor.toFixed(4)))
  }, [])

  const setCurrentItemIndexWithScroll = useCallback((index: number, scrollIntoView: boolean = false) => {
    setCurrentItemIndex(index)
    setScrollIntoView(scrollIntoView ? index : undefined)
  }, [])

  return (
    <SignatureContext.Provider
      value={{
        pager: {
          currentPage,
          nextPage,
          prevPage,
          setNumPages,
          setCurrentPage,
          numPages,
        },
        fileName,
        setFileName,
        items,
        loadItems,
        resetItem,
        item: items[currentItemIndex],
        setItems,
        addItem,
        updateItem,
        addSigningToAll,
        removeItem,
        currentItemIndex,
        setCurrentItemIndex,
        setCurrentItemIndexWithScroll,
        scrollIntoView,
        setScrollIntoView,
        placingMode,
        setPlacingMode,
        setSigningMode,
        signingMode,
        resetContext,
        resetFields,
        pageSize,
        setPageSize: setPageSizeWithCalc,
        openSigningModal,
        allSigned,
        pageScaleFactor,
        popoverOpenForIndex,
        openRelatedPopoverForIndex,
      }}>
      {children}
      {modalState.open && <SignatureModal
        isOpen={true}
        signature={items[currentItemIndex]?.signature}
        onClose={() => setModalState({ open: false })}
        onDone={handleSigning}
      />}
    </SignatureContext.Provider>
  )
}

export const useSignatureContext = (): SignatureContextValue => {
  const context = useContext(SignatureContext)
  if (!context) {
    throw new Error('useSignatureContext must be used within SignatureContextProvider')
  }
  return context
}

export default SignatureContextProvider
